// Handle.tsx
//
// O ENDEREÇO PÚBLICO do restaurante: `<handle>.alfredo.food`.
//
// O aviso desta tela foi MEDIDO antes de ser escrito, e o texto que parecia
// óbvio estava errado. Com o curinga `*.alfredo.food` no ar (DNS e certificado
// já emitidos), um endereço novo responde na hora: um host sorteado deu 200 OK
// com HTTPS válido em 0,76 s, sem cadastrar nada. Ou seja, "o domínio novo leva
// algumas horas" seria mentira.
//
// O que dói de verdade na troca é o LINK ANTIGO — e ele não morre: o Alfredo
// guarda o endereço anterior e redireciona (308) para o novo. O que não dá para
// consertar é o cardápio impresso e o QR de mesa que já estão na rua mostrando
// um endereço que não é mais o principal. É isso que a tela diz.

import React, { useEffect, useRef, useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/storesSection/Site";
import { DOMINIO_DO_SITE, normalizarHandle, sugerirHandle } from "../../../../comum/site";

interface Props {
  nomeDaMarca: string;
  handleAtual: string | null;
  aposentados: { handle: string; aposentadoEm: string | null }[];
  salvando: boolean;
  onSalvar: (handle: string) => void;
}

const Handle: React.FC<Props> = ({
  nomeDaMarca,
  handleAtual,
  aposentados,
  salvando,
  onSalvar,
}) => {
  const [valor, setValor] = useState(handleAtual ?? sugerirHandle(nomeDaMarca));
  const [confirmando, setConfirmando] = useState(false);
  const tocado = useRef(false);

  /*
   * SEMEAR DEPOIS QUE O DADO CHEGA.
   *
   * O `useState` acima roda no PRIMEIRO render, quando `nomeDaMarca` ainda é ""
   * (a section só sabe qual marca é depois do efeito que escolhe o escopo) e
   * `handleAtual` ainda é null. Sem este efeito o campo nascia VAZIO — e como o
   * placeholder é justamente a sugestão, ele PARECIA preenchido e o botão
   * ficava desabilitado sem explicação. Foi assim na primeira captura.
   *
   * `tocado` existe para o efeito não desfazer o que o dono digitou.
   */
  useEffect(() => {
    if (tocado.current) return;
    const semente = handleAtual ?? sugerirHandle(nomeDaMarca);
    if (semente) setValor(semente);
  }, [handleAtual, nomeDaMarca]);

  // A validação é a MESMA função do backend (módulo compartilhado, hash travado
  // no build). Duas listas de reservados divergentes é o defeito do Raio: lá o
  // middleware conhece 7 nomes e o backend 12, e um nome aceito na gravação
  // nunca resolve.
  let erroDeFormato: string | null = null;
  try {
    if (valor) normalizarHandle(valor);
  } catch (erro: any) {
    erroDeFormato = erro?.message ?? "Endereço inválido.";
  }

  const mudou = valor.trim().toLowerCase() !== (handleAtual ?? "");
  const podeSalvar = Boolean(valor) && !erroDeFormato && mudou && !salvando;

  return (
    <AlfredoStyles.Cartao>
      <AlfredoStyles.CartaoTitulo>Endereço do site</AlfredoStyles.CartaoTitulo>
      <AlfredoStyles.CartaoSubtitulo>
        É o endereço que o seu cliente digita e que você imprime no cardápio.
      </AlfredoStyles.CartaoSubtitulo>

      {handleAtual && (
        <AlfredoStyles.EnderecoPublico style={{ marginBottom: "var(--sp-16)" }}>
          <AlfredoStyles.MaterialIcons>language</AlfredoStyles.MaterialIcons>
          {handleAtual}
          <AlfredoStyles.Sufixo>.{DOMINIO_DO_SITE}</AlfredoStyles.Sufixo>
        </AlfredoStyles.EnderecoPublico>
      )}

      <AlfredoStyles.Linha style={{ alignItems: "flex-end" }}>
        <AlfredoStyles.Campo style={{ minWidth: "calc(var(--u) * 260)" }}>
          <span>{handleAtual ? "Trocar para" : "Escolha o endereço"}</span>
          <AlfredoStyles.Entrada
            value={valor}
            onChange={(e) => {
              tocado.current = true;
              setValor(e.target.value.toLowerCase());
              setConfirmando(false);
            }}
            /* Placeholder NEUTRO. Já foi `famiglialucco` e isso era um convite ao
               erro em duas frentes: o campo vazio PARECIA preenchido, e o nome
               sugerido era o de um restaurante específico — justamente o que a
               apuração mostrou estar trocado no cadastro. */
            placeholder="nomedoseurestaurante"
          />
          <AlfredoStyles.CampoAjuda>
            Só letras minúsculas, números e hífen. O site fica em{" "}
            <strong>
              {valor || "seuendereco"}.{DOMINIO_DO_SITE}
            </strong>
            .
          </AlfredoStyles.CampoAjuda>
        </AlfredoStyles.Campo>

        {!confirmando ? (
          <AlfredoStyles.Button
            type="button"
            disabled={!podeSalvar}
            onClick={() => (handleAtual ? setConfirmando(true) : onSalvar(valor))}
          >
            {handleAtual ? "Trocar endereço" : "Definir endereço"}
          </AlfredoStyles.Button>
        ) : (
          <>
            <AlfredoStyles.Button
              type="button"
              color="#8f5e07"
              disabled={salvando}
              onClick={() => {
                setConfirmando(false);
                onSalvar(valor);
              }}
            >
              Confirmo a troca
            </AlfredoStyles.Button>
            <AlfredoStyles.Button
              type="button"
              disabled={salvando}
              onClick={() => setConfirmando(false)}
            >
              Cancelar
            </AlfredoStyles.Button>
          </>
        )}
      </AlfredoStyles.Linha>

      {erroDeFormato && valor && (
        <AlfredoStyles.Aviso $tom="erro">{erroDeFormato}</AlfredoStyles.Aviso>
      )}

      {confirmando && (
        // O texto abaixo é o que a medição autorizou dizer. Nada sobre espera.
        <AlfredoStyles.Aviso $tom="atencao">
          <strong>Trocar o endereço do site.</strong>{" "}
          {handleAtual}.{DOMINIO_DO_SITE} passa a ser {valor}.{DOMINIO_DO_SITE} na
          hora, sem espera. O endereço antigo continua funcionando e redireciona para
          o novo — mas cardápio impresso, QR de mesa e post antigo passam a mostrar
          um endereço que não é mais o seu. Trocar depois de divulgar não é
          recomendado.
        </AlfredoStyles.Aviso>
      )}

      {aposentados.length > 0 && (
        <AlfredoStyles.CampoAjuda style={{ display: "block", marginTop: "var(--sp-16)" }}>
          Endereços antigos que continuam redirecionando para cá:{" "}
          {aposentados.map((item) => `${item.handle}.${DOMINIO_DO_SITE}`).join(", ")}.
        </AlfredoStyles.CampoAjuda>
      )}
    </AlfredoStyles.Cartao>
  );
};

export default Handle;
